import { entityType } from 'app/utilities/format';
import Error from './error/error-page';
import Helmet from 'react-helmet';
import { isAppProd } from 'config/env';
import JsonLd from 'app/utilities/JsonLd';
import NotFound from './error/not-found-page';
import PropTypes from 'prop-types';
import React from 'react';
import { RESPONSE } from 'config/api';
import { siteTitle } from 'config/seo';
import Template from 'app/components/templates/index';
import {
    getBreadcrumbStructuredData,
    getPageCanonicalUrl,
    getStructuredDataForPage
} from 'app/utilities/seo';

const ContentPage = ({ page }) => {
    // Render not error/not found page
    if (!page || page.error && page.error.status === RESPONSE.INTERNAL_SERVER_ERROR) return <Error />;
    if (page.error && page.error.status === RESPONSE.NOT_FOUND || !page.attributes) return <NotFound />;

    const {
        attributes,
        type
    } = page;

    // Prevent homepage from showing page title
    const pageTitle = attributes.slug === 'home' ? siteTitle : `${attributes.seo_title}`;
    const image = attributes.main_images && attributes.main_images[0] || attributes.thumbnail;
    const pageUrl = getPageCanonicalUrl(page);
    const isNoIndex = !isAppProd || !attributes.is_published;
    // Venue, room, and homepage templates emit their own page graphs.
    const templateOwnsPageSchema = attributes.slug === 'home' ||
        type === 'venues' ||
        type === 'rooms';
    const pageStructuredData = templateOwnsPageSchema ? null : getStructuredDataForPage(page, {
        description: attributes.seo_description || attributes.short_description,
        image,
        title: pageTitle,
        url: pageUrl
    });
    const breadcrumbStructuredData = getBreadcrumbStructuredData(page);

    return (
        <main className="page" role="main">
            <Helmet>
                {isNoIndex && <meta name="robots" content="noindex, nofollow" />}

                <title>{pageTitle}</title>
                <meta name="description" content={attributes.seo_description} />
                <meta name="keywords" content={attributes.seo_keywords} />
                <link rel="canonical" href={pageUrl} />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={attributes.seo_description} />
                {image && <meta name="twitter:image" content={image} />}

                {/* Open Graph */}
                <meta property="og:locale" content="en_NZ" />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={attributes.seo_description} />
                <meta property="og:url" content={pageUrl} />
                {image && <meta property="og:image" content={image} />}
            </Helmet>
            <JsonLd item={pageStructuredData} />
            <JsonLd item={breadcrumbStructuredData} />
            <Template
                type={entityType(type)}
                data={attributes}
            />
        </main>
    );
};

ContentPage.propTypes = {
    page: PropTypes.object.isRequired
};

export default ContentPage;
